/**
 * WorkCalendar — a READ-ONLY view answering "when does everyone work?" at a glance:
 * the organization's default work week, and the holiday calendar for one location.
 *
 * IT DOES NOT EDIT. That is deliberate, decided 2026-08-12 after the page's edit paths
 * were audited, and it is not a caution about confirmation dialogs — the page could not
 * correctly represent what it was editing:
 *
 *   1. The work-week dots wrote `rest_days` on the GLOBAL rule via `updateGlobal`, while
 *      this file contained no notion of department or group rules at all. Its own hint
 *      admitted they "can override it", so for any org with a department rule the dots
 *      displayed — and edited — a work week that governed nobody. A confirmation dialog
 *      on a misleading control only confirms the misleading action.
 *   2. Every write was one tap, immediate and unconfirmed, including a DELETE: the hint
 *      literally taught "click an existing holiday to remove it".
 *   3. The guardrail was on the OTHER surface. The Rules pages warn when all seven days
 *      are marked rest; here seven taps set exactly that, silently — and that is the
 *      configuration that erased a live tenant's worked hours (see migration 097 and
 *      `tests/attendance/test_famaka_cycle_and_rest_days.py` in svdy-platform).
 *   4. Neither surface said the thing that actually matters: changing the work week or a
 *      holiday invalidates attendance ALREADY CALCULATED, and the 6-hourly recalc cron
 *      only revisits each org's local yesterday + today. Editing last month's holiday
 *      therefore never changed last month's report.
 *
 * So the two settings keep their single owners, which is where the guardrails and the
 * recalculation notice live:
 *
 *   work week      -> /attendance/rules              (Global / Department / Group)
 *   public holidays-> /attendance/leave/public-holidays
 *
 * The original docstring claimed this page "replaces the rest_days control previously
 * buried in the Rules pages". It never did — the control stayed, so the field had two
 * editors with different rules, which is the drift this reverses.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Snackbar, Alert, IconButton, Chip,
  Tooltip, Button, FormControl, FormHelperText, InputLabel, MenuItem, Select,
} from '@mui/material';
import { ChevronLeft, ChevronRight, CelebrationOutlined, EditOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { attendanceApi, organizationApi } from '../../../api';
import type { AttendanceRule, PublicHoliday } from '../../../api/attendance';
import type { LocationNode } from '../../../api/organization';
import { activeLocale } from '../../../utils/datetime';

import { isRestDaySelected } from '../Rules/ruleSections';

const WEEKDAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Where each setting is actually changed.
 *
 * These are FULL route paths, and the rules one carries its `/global` segment: the router
 * declares `rules/global`, `rules/department` and `rules/group` — there is no
 * `/attendance/rules` parent route. The first version of this file linked to the bare
 * `/attendance/rules`, which matched nothing, fell through to the catch-all and landed the
 * admin on /dashboard. The organization default work week is edited on the GLOBAL page, so
 * that is the segment to name.
 *
 * `navigateTargets.test.ts` now resolves a `to={CONSTANT}` and checks these against the
 * parsed route table. Its four existing patterns all read a string LITERAL, so a constant
 * — which is otherwise the better way to write this — was invisible to every one of them.
 */
export const WORK_WEEK_EDIT_PATH = '/attendance/rules/global';
export const HOLIDAYS_EDIT_PATH = '/attendance/leave/public-holidays';

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const WorkCalendar: React.FC = () => {
  const { t } = useTranslation();
  const [rule, setRule] = useState<AttendanceRule | null>(null);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [locationId, setLocationId] = useState('');
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const metaGenerationRef = useRef(0);
  const holidayGenerationRef = useRef(0);
  const hasLoadedHolidaysRef = useRef(false);

  useEffect(() => {
    const generation = ++metaGenerationRef.current;
    const loadMeta = async () => {
      setLoading(true);
      try {
        const [nextRule, locationRows] = await Promise.all([
          attendanceApi.rulesApi.getGlobal(),
          organizationApi.locationsApi.list(),
        ]);
        if (generation !== metaGenerationRef.current) return;
        setRule(nextRule);
        setLocations(locationRows);
        if (locationRows.length > 0) setLocationId((prev) => prev || locationRows[0].id);
      } catch {
        if (generation === metaGenerationRef.current) {
          setToast({ msg: t('attendance:workCalendar.loadFailed'), sev: 'error' });
        }
      } finally {
        if (generation === metaGenerationRef.current) setLoading(false);
      }
    };
    void loadMeta();
    return () => { metaGenerationRef.current += 1; };
  }, [t]);

  useEffect(() => {
    if (!locationId) return undefined;
    const generation = ++holidayGenerationRef.current;
    const loadHolidays = async () => {
      try {
        // (locationId, includeLegacy) — positional, and includeLegacy stays TRUE: an
        // org-wide holiday with no location still applies to this site, and a view that
        // hid it would under-report the calendar it exists to show.
        const rows = await attendanceApi.publicHolidaysApi.list(locationId, true);
        if (generation !== holidayGenerationRef.current) return;
        setHolidays(rows);
        hasLoadedHolidaysRef.current = true;
      } catch {
        if (generation === holidayGenerationRef.current && !hasLoadedHolidaysRef.current) {
          setToast({ msg: t('attendance:workCalendar.loadFailed'), sev: 'error' });
        }
      }
    };
    void loadHolidays();
    return () => { holidayGenerationRef.current += 1; };
  }, [locationId, t]);

  const restDays: string[] = (rule?.rest_days as string[] | undefined) ?? [];

  const holidayByDate = useMemo(() => {
    const m = new Map<string, PublicHoliday>();
    // Org-wide rows first, then location rows, so a location-specific holiday wins the cell.
    for (const holiday of holidays) {
      if (holiday.date && holiday.location_id == null) m.set(holiday.date as string, holiday);
    }
    for (const holiday of holidays) {
      if (holiday.date && holiday.location_id != null) m.set(holiday.date as string, holiday);
    }
    return m;
  }, [holidays]);

  // Build the month grid (Mon-first).
  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const out: (Date | null)[] = [];
    for (let i = 0; i < startOffset; i += 1) out.push(null);
    const dim = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= dim; d += 1) out.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [month]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;
  }

  const monthLabel = month.toLocaleDateString(activeLocale(), { month: 'long', year: 'numeric' });
  const todayKey = ymd(new Date());
  const restCount = WEEKDAY_ABBR.filter((abbr) => isRestDaySelected(restDays, abbr)).length;
  const monthHolidayCount = cells.filter((d) => d && holidayByDate.has(ymd(d))).length;

  /** Header row of a card: title on the left, the owning page's Edit link on the right. */
  const cardHeader = (title: string, to: string, editLabel: string, extra?: React.ReactNode) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {extra}
        <Button
          component={RouterLink}
          to={to}
          size="small"
          startIcon={<EditOutlined fontSize="small" />}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
        >
          {editLabel}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('attendance:workCalendar.title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('attendance:workCalendar.subtitle')}</Typography>
      </Box>

      <FormControl size="small" sx={{ minWidth: 240, mb: locations.length === 0 ? 1 : 2 }} required>
        <InputLabel>{t('attendance:workCalendar.locationLabel')}</InputLabel>
        <Select value={locationId} label={t('attendance:workCalendar.locationLabel')} onChange={(event) => { holidayGenerationRef.current += 1; setLocationId(event.target.value); }}>
          {locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)}
        </Select>
        <FormHelperText>{t('attendance:workCalendar.locationHelperView')}</FormHelperText>
      </FormControl>
      {locations.length === 0 && <Alert icon={false} severity="info" sx={{ width: '100%', maxWidth: 720, mb: 2 }}>{t('attendance:workCalendar.noLocationHint')}</Alert>}

      {/* Work week — display only */}
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 720, p: 2.5, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        {cardHeader(t('attendance:workCalendar.workWeek'), WORK_WEEK_EDIT_PATH, t('attendance:workCalendar.editWorkWeek'))}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>{t('attendance:workCalendar.workWeekViewHint')}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {WEEKDAY_ABBR.map((abbr) => {
            const isRest = isRestDaySelected(restDays, abbr);
            return (
              <Tooltip key={abbr} title={isRest ? t('attendance:workCalendar.restDay') : t('attendance:workCalendar.workDay')}>
                {/* A plain Box, not an IconButton: nothing here is pressable. */}
                <Box
                  aria-label={`${t(`attendance:shiftCycles.weekday.${abbr.toLowerCase()}`)} — ${isRest ? t('attendance:workCalendar.restDay') : t('attendance:workCalendar.workDay')}`}
                  sx={{
                    width: 52, height: 52, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isRest ? 'grey.100' : 'primary.main',
                    color: isRest ? 'text.disabled' : '#fff',
                    border: '2px solid', borderColor: isRest ? 'divider' : 'primary.main',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                    {t(`attendance:shiftCycles.weekday.${abbr.toLowerCase()}`)}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {restCount === 7
            ? t('attendance:workCalendar.statsAllRest')
            : t('attendance:workCalendar.statsWorkWeek', { work: 7 - restCount, rest: restCount })}
        </Typography>
      </Paper>

      {/* Holidays calendar — display only */}
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 720, p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        {cardHeader(
          t('attendance:workCalendar.holidays'), HOLIDAYS_EDIT_PATH, t('attendance:workCalendar.editHolidays'),
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" aria-label={t('attendance:workCalendar.previousMonth')} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><ChevronLeft fontSize="small" /></IconButton>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 130, textAlign: 'center' }}>{monthLabel}</Typography>
            <IconButton size="small" aria-label={t('attendance:workCalendar.nextMonth')} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><ChevronRight fontSize="small" /></IconButton>
          </Box>,
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{t('attendance:workCalendar.holidaysViewHint')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
          {WEEKDAY_ABBR.map((abbr) => (
            <Typography key={abbr} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}>
              {t(`attendance:shiftCycles.weekday.${abbr.toLowerCase()}`)}
            </Typography>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {cells.map((d, i) => {
            if (!d) return <Box key={i} sx={{ minHeight: 56 }} />;
            const key = ymd(d);
            const hol = holidayByDate.get(key);
            const isOrgWide = hol ? hol.location_id == null : false;
            const isRest = isRestDaySelected(restDays, WEEKDAY_ABBR[(d.getDay() + 6) % 7]);
            const cell = (
              <Box
                sx={{
                  minHeight: 56, p: 0.5, borderRadius: 1,
                  border: '1px solid', borderColor: key === todayKey ? 'primary.main' : 'divider',
                  bgcolor: hol ? 'rgba(237,108,2,0.10)' : isRest ? 'grey.50' : '#fff',
                  display: 'flex', flexDirection: 'column',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{d.getDate()}</Typography>
                {hol && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 'auto' }}>
                    <CelebrationOutlined sx={{ fontSize: 12, color: 'warning.main' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'warning.dark', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hol.name}
                    </Typography>
                    {isOrgWide && <Chip label={t('attendance:workCalendar.allLocationsShort')} size="small" variant="outlined" sx={{ height: 14, fontSize: '0.52rem', borderRadius: 1, '& .MuiChip-label': { px: 0.5 } }} />}
                  </Box>
                )}
              </Box>
            );
            // Only a cell that has something to say carries a tooltip. An empty date used to
            // show "click to add", which is exactly the instruction being withdrawn.
            return hol
              ? (
                <Tooltip key={i} title={isOrgWide ? t('attendance:workCalendar.allLocationsHoliday', { name: hol.name }) : hol.name}>
                  {cell}
                </Tooltip>
              )
              : <Box key={i}>{cell}</Box>;
          })}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {t('attendance:workCalendar.statsHolidays', { count: monthHolidayCount, month: monthLabel })}
        </Typography>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast?.sev ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkCalendar;
